import { Box, Flex, Field, Text } from "@chakra-ui/react";
import { Heading, Radio, RadioGroup } from "@digital-services-gard-as/anchor-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InvoiceFormData } from "@/types";
import { FormNumberInput } from "./FormNumberInput";
import { useCallback } from "react";

export const LawyerAndCounselFees = () => {
    const { t } = useTranslation(["invoices", "main"]);
    const { watch, setValue } = useFormContext<InvoiceFormData>();

    const hasLawyerAndCounselFees = watch("hasLawyerAndCounselFees");
    const handleLawyerAndCounselFeesRadioChange = useCallback(
        (value: string) => {
            setValue("hasLawyerAndCounselFees", value === "true");
        },
        [setValue]
    );

    return (
        <Box bg="white" p={{ base: "1rem", md: "1.5rem" }} borderRadius="md" boxShadow="xs">
            <Heading size="md" style={{ marginBottom: "0.5rem" }}>
                {t("createInvoice.feesAndExpenses.lawyerCounselCourtFees")}
            </Heading>
            <Field.Root mb={4}>
                <RadioGroup
                    legend={t("createInvoice.feesAndExpenses.lawyerOrCourtArbitratorFees")}
                    value={hasLawyerAndCounselFees?.toString()}
                    onChange={handleLawyerAndCounselFeesRadioChange}
                    data-testid="hasLawyerAndCounselFees"
                >
                    <Radio value="true" size="sm">
                        <Text fontSize="md">{t("main:yes")}</Text>
                    </Radio>
                    <Radio value="false" size="sm">
                        <Text fontSize="md">{t("main:no")}</Text>
                    </Radio>
                </RadioGroup>
            </Field.Root>
            <ShowLawyerAndCounselFeesInputs hasLawyerAndCounselFees={hasLawyerAndCounselFees} />
        </Box>
    );
};

type ShowLawyerAndCounselFeesInputsProps = {
    hasLawyerAndCounselFees: boolean | undefined;
};

const ShowLawyerAndCounselFeesInputs = ({ hasLawyerAndCounselFees }: ShowLawyerAndCounselFeesInputsProps) => {
    const { t } = useTranslation("invoices");
    const {
        watch,
        control,
        formState: { errors }
    } = useFormContext<InvoiceFormData>();
    const currency = watch("bankAccount.currency");

    if (!hasLawyerAndCounselFees) {
        return null;
    }

    return (
        <>
            <Flex gap="2rem" mb={4} flexWrap="wrap">
                <FormNumberInput
                    id="lawyerFees"
                    label={t("invoices:createInvoice.feesAndExpenses.lawyerFees")}
                    isRequired={false}
                    errorMessage={errors.lawyerFees?.message}
                    control={control}
                    currency={currency}
                />
                <FormNumberInput
                    id="lawyerExpenses"
                    label={t("invoices:createInvoice.feesAndExpenses.lawyerExpenses")}
                    isRequired={false}
                    errorMessage={errors.lawyerExpenses?.message}
                    control={control}
                    currency={currency}
                />
            </Flex>

            <Flex gap="2rem" flexWrap="wrap">
                <FormNumberInput
                    id="counselFees"
                    label={t("invoices:createInvoice.feesAndExpenses.counselFees")}
                    isRequired={false}
                    errorMessage={errors.counselFees?.message}
                    control={control}
                    currency={currency}
                />
                <FormNumberInput
                    id="courtArbitFees"
                    label={t("invoices:createInvoice.feesAndExpenses.courtArbitFees")}
                    isRequired={false}
                    errorMessage={errors.courtArbitFees?.message}
                    control={control}
                    currency={currency}
                />
            </Flex>
        </>
    );
};
